import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import axios from "axios";
import { FaColumns } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";

import ModifyColumns from "./Tools/ModifyColumns";
import { DailyCategoryColumns } from "./Components/Reporting/DailyColumns";
import {
  apiRequestBodyBuilder,
  FilterComponent,
  getFilteredData,
} from "./config/tableHelper";
import { RenderTable } from "./config/tableWrapper";
import { CategoryInsights } from "../components/InsightCategoryComp";

// --- Static Column Classifications ---
const COMPARE_COLUMNS = new Set([
  "percentage_difference_clicks",
  "percentage_difference_impressions",
  "percentage_difference_cost",
  "percentage_difference_ctr",
  "percentage_difference_average_cpc",
  "percentage_difference_conversions",
  "percentage_difference_conversion_rate",
  "percentage_difference_cost_per_conversion",
  "percentage_difference_interactions",
  "percentage_difference_interaction_rate",
  "current_ctr",
  "previous_ctr",
  "current_interaction_rate",
  "previous_interaction_rate",
  "current_conversion_rate",
  "previous_conversion_rate",
]);

const NUMBER_COLUMNS = new Set([
  "current_impressions",
  "previous_impressions",
  "current_cost",
  "previous_cost",
  "current_conversions",
  "previous_conversions",
  "current_interactions",
  "previous_interactions",
  "current_clicks",
  "previous_clicks",
]);

const CURRENCY_COLUMNS = new Set([
  "current_cost",
  "previous_cost",
  "current_average_cpc",
  "previous_average_cpc",
  "current_cost_per_conversion",
  "previous_cost_per_conversion",
]);

// --- Helper Functions ---
const formatNumberWithCommas = (val) => {
  const num = typeof val === "number" ? val.toFixed(2) : String(val);
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const flattenCampaignGroupData = (groupData) => {
  const flattened = {};
  const nestedKeys = [
    "current_totals",
    "previous_totals",
    "percentage_difference",
  ];

  for (const [key, value] of Object.entries(groupData || {})) {
    if (
      nestedKeys.includes(key) &&
      typeof value === "object" &&
      value !== null
    ) {
      const prefix = key.replace("_totals", "");
      for (const [subKey, subVal] of Object.entries(value)) {
        flattened[`${prefix}_${subKey}`] = subVal;
      }
    } else {
      flattened[key] = value;
    }
  }
  return flattened;
};

// --- Table Skeleton Loader ---
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Main Component ---
const CategoryReporting = forwardRef((props, ref) => {
  const { previousDates = {}, categoryType } = props;

  // Table & Column State
  const [, setTableVisible] = useState(false);
  const [columns, setColumns] = useState(() =>
    DailyCategoryColumns.map((col) =>
      col.key.includes("percentage_difference")
        ? { ...col, type: "percentage-multi" }
        : col,
    ),
  );
  const [, setTotal] = useState({});
  const [, setTotalShow] = useState(true);
  const [currencySymbol] = useState("₹");
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const columnRef = useRef(null);

  // Data & Filter State
  const [data, setData] = useState([]);
  const [, setGroups] = useState([]);
  const [filterArray, setFilterArray] = useState([]);
  const [loading, setLoading] = useState(true);

  // Insights State
  const [insights, setInsights] = useState([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [editedInsights, setEditedInsights] = useState([]);

  // Filtered table rows
  const filteredData = useMemo(() => {
    return getFilteredData(data, filterArray);
  }, [data, filterArray]);

  // Imperative handle for parent-triggered actions (e.g. Export HTML)
  useImperativeHandle(ref, () => ({
    generateCategoryHtmlTable: () => {
      if (!filteredData || filteredData.length === 0) {
        return "<p>No category data available.</p>";
      }

      const visibleCols = columns.filter((col) => col.visible);
      const headerRow = visibleCols
        .map(
          (col) =>
            `<th style="text-transform: capitalize; background-color:#283593; color:white">${col.title}</th>`,
        )
        .join("");

      const dataRows = filteredData
        .map((item) => {
          const cells = visibleCols.map((col) => {
            let value = item[col.key];
            if (typeof value === "number") {
              if (CURRENCY_COLUMNS.has(col.key)) {
                value = currencySymbol + formatNumberWithCommas(value);
              } else if (NUMBER_COLUMNS.has(col.key)) {
                if (value > 1000000) value = value / 1000000;
                value = formatNumberWithCommas(value);
              } else if (
                COMPARE_COLUMNS.has(col.key) ||
                col.type === "percentage-multi"
              ) {
                value = `${value > 0 ? "+" : ""}${Number(value).toFixed(2)}%`;
              }
            }
            return `<td>${value ?? ""}</td>`;
          });
          return `<tr>${cells.join("")}</tr>`;
        })
        .join("");

      return `
        <h3><strong>Category Reporting</strong></h3>
        <table border="1" cellpadding="6" style="border-collapse: collapse; margin-bottom: 20px;">
          <thead style="background-color: #283593;"><tr>${headerRow}</tr></thead>
          <tbody>${dataRows}</tbody>
        </table>
      `;
    },
  }));

  // Fetch campaign groups
  const fetchGroups = useCallback(async () => {
    try {
      const requestBody = apiRequestBodyBuilder();
      const response = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const resData = await response.json();
      setGroups(resData.groups || []);
    } catch (err) {
      console.error("Failed to fetch campaign groups:", err);
    }
  }, []);

  // Fetch group metrics
  const fetchGroupedMetrics = useCallback(async () => {
    try {
      const res = await axios.post(
        "https://api.confidanto.com/campaign-group/campaign-curr-prev-group-metrcis",
        {
          customer_id: localStorage.getItem("customer_id"),
          email: localStorage.getItem("email"),
          project_id: localStorage.getItem("project_id"),
          ad_account_id: localStorage.getItem("ad_account_id"),
          platform: localStorage.getItem("platform"),
          start_date: previousDates.curr_start_date,
          end_date: previousDates.curr_end_date,
          previous_start_date: previousDates.prev_start_date,
          previous_end_date: previousDates.prev_end_date,
          category_type: categoryType,
        },
      );

      setData(
        Array.isArray(res.data) ? res.data.map(flattenCampaignGroupData) : [],
      );
    } catch (err) {
      console.error("Failed to fetch grouped metrics:", err);
    }
  }, [previousDates, categoryType]);

  // Fetch Category Insights
  const fetchCategoryInsights = useCallback(async () => {
    setInsightLoading(true);
    try {
      const reqBody = apiRequestBodyBuilder(null, {
        start_date: previousDates.curr_start_date,
        end_date: previousDates.curr_end_date,
        insight_type: categoryType,
      });

      const res = await fetch(
        "https://api.confidanto.com/get-insights/category-insights",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reqBody),
        },
      );
      const result = await res.json();
      setInsights(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Failed to fetch category insights:", err);
    } finally {
      setInsightLoading(false);
    }
  }, [
    previousDates.curr_start_date,
    previousDates.curr_end_date,
    categoryType,
  ]);

  // Initial table data load
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([fetchGroups(), fetchGroupedMetrics()]);
      setLoading(false);
    };

    fetchAllData();
  }, [fetchGroups, fetchGroupedMetrics]);

  // Fetch insights when dates change
  useEffect(() => {
    fetchCategoryInsights();
  }, [fetchCategoryInsights]);

  // Dropdown click-outside handler
  useEffect(() => {
    if (!showColumnsMenu) return;

    const handleClickOutside = (event) => {
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnsMenu]);

  // Filter actions
  const removeFilter = (id) => {
    setFilterArray((prev) => prev.filter((item) => item.id !== id));
  };

  // Insight edit handlers
  const handleEditClick = () => {
    setEditedInsights(insights.map((insight) => ({ ...insight })));
    setIsEditingInsight(true);
  };

  const handleInsightChange = (index, newValue) => {
    setEditedInsights((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], insight_text: newValue };
      return updated;
    });
  };

  const handleSaveInsight = () => {
    setInsights(editedInsights);
    setIsEditingInsight(false);
  };

  const handleCancelEdit = () => {
    setIsEditingInsight(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold text-left text-blue-600 flex flex-col my-4 items-start">
        Category wise
      </h2>

      <div className="font-roboto">
        <div className="bg-gray-100 p-4 shadow border border-gray-200">
          <div className="flex flex-row justify-between items-center mb-4">
            {/* Filter Section: Dropdown + Active Badges */}
            <div className="flex flex-row justify-center items-start gap-3">
              <FilterComponent
                columns={columns}
                data={data}
                filterArray={filterArray}
                setFilterArray={setFilterArray}
              />

              <div className="flex flex-nowrap whitespace-nowrap gap-2">
                {filterArray.map((filter) => (
                  <div
                    key={filter.id}
                    className="flex items-center bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-md text-sm font-medium shadow-sm"
                  >
                    <span className="mr-2">
                      {filter.title} {filter.sign}{" "}
                      {Array.isArray(filter.value)
                        ? filter.value.join(", ")
                        : filter.value}
                    </span>
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="text-blue-400 hover:text-blue-600 transition-colors focus:outline-none"
                    >
                      <IoMdClose size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Column Visibility Selector */}
            <div className="relative" ref={columnRef}>
              <button
                type="button"
                className="bg-transparent text-gray-600 px-4 py-2 rounded flex flex-col justify-center items-center hover:bg-slate-100 transition-colors"
                onClick={() => setShowColumnsMenu((prev) => !prev)}
              >
                <FaColumns className="mr-2" />
                <p>Columns</p>
              </button>

              {showColumnsMenu && (
                <div className="absolute right-0 z-50">
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setShowColumnsMenu={setShowColumnsMenu}
                    setTotal={setTotal}
                    setTotalShow={setTotalShow}
                    colsLimit={13}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto bg-white rounded shadow">
            {loading ? (
              <TableSkeleton />
            ) : filteredData.length === 0 ? (
              <div className="flex justify-center items-center w-full h-44 text-gray-500 font-medium">
                No Data Found
              </div>
            ) : (
              <RenderTable
                columns={columns}
                data={filteredData}
                variant="reporting"
                hideTotal={true}
              />
            )}
          </div>
        </div>

        {/* Insights Section */}
        <CategoryInsights
          insights={insights}
          insightLoading={insightLoading}
          isEditing={isEditingInsight}
          editedInsights={editedInsights}
          onEditClick={handleEditClick}
          onSave={handleSaveInsight}
          onCancel={handleCancelEdit}
          onInsightChange={handleInsightChange}
        />
      </div>
    </div>
  );
});

CategoryReporting.displayName = "CategoryReporting";

export default CategoryReporting;
